/**
 * Informe de inspección previa en lenguaje RIO 360 (Diego 2026-09-17).
 * Un arribo, una inspección: la banda nombra la operación que la tuvo y sus
 * parciales de nacionalización. Tiras de conteo, resultado por producto en
 * el orden de la factura (faltantes y sobrantes primero, diferencia en
 * número y decisión al lado), acta como línea de tiempo y fotos de la tarea.
 * El resultado sale del conteo, no solo de la novedad; "sin conteo" es un
 * estado visible. Para clasificadores, especialistas y ejecutivos.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-08-20 · RIO 360 2026-09-17
 */
import React, { useMemo, useState } from "react";
import { Button } from "codekit";
import { useGetInformeInspeccionQuery } from "../../../libs/redux/slices/Modimpxx/Inspeccion/inspeccionApiSlice";
import { openGenioVisorByDo } from "../../../utils/genioVisor";
import Botgenio from "../../Tablerox/Botgenio";
import { tono } from "../../Tablerox/tonos";
import "../../Tablerox/riodo.css";
import "./infoinsp.css";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const fechaCorta = (fecha, hora = "") => {
  if (!fecha || fecha === "0000-00-00") return "";
  const [, mes, dia] = String(fecha).split("-");
  const texto = `${Number(dia)} ${MESES[Number(mes) - 1] ?? ""}`;
  return hora ? `${texto} · ${String(hora).substring(0, 5)}` : texto;
};
const numero = (valor) => (valor === null || valor === undefined ? "—" : Number(valor).toLocaleString("es-CO", { maximumFractionDigits: 5 }));
const ORDEN = { FALTANTE: 0, SOBRANTE: 1, SIN_CONTEO: 2, CONFORME: 3 };
const RESULTADO = {
  FALTANTE: { texto: "Faltante", tonoxxx: "ROJO" },
  SOBRANTE: { texto: "Sobrante", tonoxxx: "NARANJA" },
  CONFORME: { texto: "Conforme", tonoxxx: "VERDE" },
  SIN_CONTEO: { texto: "Sin conteo registrado", tonoxxx: "GRIS" },
};

const Chip = ({ tonoxxx = "GRIS", children, title }) => (
  <span className="rio-chip" style={tono(tonoxxx).chip} title={title}>{children}</span>
);

/** Foto en grande dentro del informe (Diego 2026-09-17): con volver y flechas, sin salir de la pantalla */
const Ampliada = ({ fotos, indice, producto, onCerrar, onMover }) => {
  const foto = fotos[indice];
  if (!foto) return null;
  return (
    <div className="iip-velo iip-velo-zoom" onClick={onCerrar}>
      <div className="iip-zoom" onClick={(e) => e.stopPropagation()}>
        <div className="iip-zoom-cab">
          <Button type="button" variant="secondary" size="s" icon="back-arrow" onClick={onCerrar}>Volver a las fotos</Button>
          <span className="iip-zoom-titulo">Foto {indice + 1} de {fotos.length}{foto.MAKEDATE ? ` · ${foto.MAKEDATE}` : ""}</span>
          {producto && (
            <span className="iip-zoom-item">
              Ítem {producto.SECITEXX} · <b>{producto.PROIDXXX}</b>{producto.FACIDXXX ? ` · Factura ${producto.FACIDXXX}` : ""}
            </span>
          )}
          <Botgenio size="s" title="Abrir el archivo en Genio Documental" style={{ marginLeft: "auto" }} onClick={() => window.open(foto.URLFOTOX, "_blank", "noopener,noreferrer")}>Abrir en Genio</Botgenio>
        </div>
        <div className="iip-zoom-cuerpo">
          <button type="button" className="iip-zoom-flecha" onClick={() => onMover(-1)} disabled={indice === 0} aria-label="Anterior">‹</button>
          <img src={foto.URLFOTOX} alt={`Foto ${foto.IDARCHXX}`} />
          <button type="button" className="iip-zoom-flecha" onClick={() => onMover(1)} disabled={indice >= fotos.length - 1} aria-label="Siguiente">›</button>
        </div>
      </div>
    </div>
  );
};

const Galeria = ({ titulo, fotos, descriptores = [], seriales = [], producto = null, onCerrar }) => {
  const [ampliada, setAmpliada] = useState(null);
  return (
  <div className="iip-velo" onClick={onCerrar}>
    {ampliada !== null && (
      <Ampliada fotos={fotos} indice={ampliada} producto={producto} onCerrar={() => setAmpliada(null)} onMover={(paso) => setAmpliada(Math.min(fotos.length - 1, Math.max(0, ampliada + paso)))} />
    )}
    <div className="rio-card iip-galeria" onClick={(e) => e.stopPropagation()}>
      <div className="iip-galeria-cab">
        <div><div className="rio-kicker">Fotos y ficha de campo</div><div className="rio-card-title">{titulo} · {fotos.length} fotos</div></div>
        <button type="button" className="rio-icon-btn" onClick={onCerrar} aria-label="Cerrar">×</button>
      </div>
      {descriptores.length > 0 && (
        <div className="iip-ficha">
          <div className="rio-kicker">Ficha verificada en campo</div>
          <div className="iip-ficha-grid">
            {descriptores.map((d, i) => (
              <div key={i}><span className="iip-l">{d.DESDESXX}</span>{d.PRDESVAX || "—"}</div>
            ))}
          </div>
        </div>
      )}
      {seriales.length > 0 && (
        <div className="iip-ficha">
          <div className="rio-kicker">Seriales y valores capturados en campo</div>
          <div className="iip-ficha-grid">
            {seriales.map((s, i) => (
              <div key={i}><span className="iip-l">{s.DESDESXX || "Serial"}</span>{s.VALSERXX}{s.RECSCOBU ? ` · bulto ${s.RECSCOBU}` : ""}</div>
            ))}
          </div>
        </div>
      )}
      {fotos.length === 0 ? (
        <div className="iip-vacio">Sin fotos para este producto.</div>
      ) : (
        <div className="iip-fotos">
          {fotos.map((foto, i) => (
            <button type="button" key={foto.IDARCHXX} className="iip-foto-btn" onClick={() => setAmpliada(i)} title={`Ver en grande · ${foto.MAKEDATE || ""}`}>
              <img src={foto.URLFOTOX} alt={`Foto ${foto.IDARCHXX}`} loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  </div>
  );
};

const Infoinsp = ({ DOCNROXX, onVolver }) => {
  const { data: respuesta, isLoading, isError } = useGetInformeInspeccionQuery(DOCNROXX, { skip: !DOCNROXX });
  const [galeria, setGaleria] = useState(null);
  const informe = respuesta?.data ?? {};
  const cabecera = informe.CABECERA ?? {};
  const mtpi = cabecera.MTPIXXXX ?? {};
  const carga = informe.CARGA ?? {};
  const resumen = informe.RESUMEN ?? {};
  const operaciones = informe.OPERACIONES ?? [{ DOCNROXX, TIPO: "" }];
  const fotosTarea = informe.FOTOS_TAREA ?? [];
  const acta = cabecera.ACTALINE ?? (cabecera.ACTAXXXX ?? []).map((t) => ({ TEXTO: t, FECHA: "", HORA: "", USUARIO: "" }));
  const productos = useMemo(
    () => [...(informe.PRODUCTOS ?? [])].sort((a, b) => (ORDEN[a.RESULTADO] ?? 9) - (ORDEN[b.RESULTADO] ?? 9) || a.SECITEXX - b.SECITEXX),
    [informe.PRODUCTOS]
  );
  const ancla = operaciones[0]?.DOCNROXX ?? DOCNROXX;
  const parciales = operaciones.slice(1);
  const cerrada = (mtpi.ESTPROYX ?? "") === "CERRADA";
  const estadoChip = cerrada
    ? { tonoxxx: "VERDE", texto: `Cerrada${cabecera.FECCIERR ? ` · ${fechaCorta(cabecera.FECCIERR.substring(0, 10), cabecera.FECCIERR.substring(11))}` : ""}` }
    : { tonoxxx: mtpi.ESTPROYX === "EN_CAMPO" ? "NARANJA" : "GRIS", texto: (mtpi.ESTPROYX || "Sin proyección").replace("_", " ").toLowerCase() };

  return (
    <div className="riodo-escala">
      <div className="riodo iip">
        <header className="rio-band">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {onVolver && <Button type="button" variant="secondary" size="m" icon="back-arrow" onClick={onVolver}>Volver</Button>}
            <div>
              <div className="rio-band-sub">
                Diligencia de inspección previa{mtpi.PUNTOTIP ? ` · ${String(mtpi.PUNTOTIP).replace("_", " ").toLowerCase()}` : ""}
              </div>
              <div className="rio-band-title">
                Inspección previa · DO {operaciones.map((o) => o.DOCNROXX).join(" + ")}
                <span style={{ fontWeight: 600, opacity: 0.85 }}> · {carga.CLIENTE || ""}</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Chip tonoxxx={estadoChip.tonoxxx}>{estadoChip.texto}</Chip>
            <Button type="button" variant="warning" size="m" icon="genio" onClick={() => openGenioVisorByDo(ancla)}>Ver Soporte</Button>
            <Button type="button" variant="secondary" size="m" icon="file-earmark-check" onClick={() => window.print()}>Imprimir / PDF</Button>
          </div>
        </header>

        {isLoading && <div className="rio-card iip-vacio">Cargando el informe…</div>}
        {isError && <div className="rio-card iip-vacio error">No fue posible cargar el informe de inspección.</div>}
        {!isLoading && !isError && (
          <>
            <section className="rio-card iip-ficha-op">
              <div>
                <div className="iip-l">Operaciones cubiertas</div>
                <div className="iip-v">
                  {operaciones.map((o) => (
                    <span key={o.DOCNROXX} className="iip-do">{o.DOCNROXX}{o.TIPO && <Chip tonoxxx="NAVY">{o.TIPO}</Chip>}</span>
                  ))}
                </div>
                <div className="iip-sub">{parciales.length > 0 ? "Un solo arribo, una sola inspección: cubre a la madre y a sus parciales de nacionalización." : "Una guía parcializada tiene su propia inspección por manifiesto."}</div>
              </div>
              <div>
                <div className="iip-l">Gestor</div>
                <div className="iip-v">{cabecera.GESTORXX || "Sin asignar"}</div>
                <div className="iip-sub">{cabecera.TARIDXXX ? `tarea ${cabecera.TARIDXXX}` : "sin tarea de reconocimiento"}</div>
              </div>
              <div>
                <div className="iip-l">Carga</div>
                <div className="iip-v">DT {carga.DTRCDOTR || "—"}{carga.MANIFIESTO ? ` · manifiesto ${carga.MANIFIESTO}` : ""}</div>
                <div className="iip-sub">{(carga.FACTURAS ?? []).length > 0 ? `factura ${carga.FACTURAS.join(", ")}` : "sin facturas"}{carga.MANIFECHA ? ` · llegó el ${fechaCorta(carga.MANIFECHA)}` : ""}</div>
              </div>
              <div>
                <div className="iip-l">Línea de la diligencia</div>
                <div className="iip-v">
                  {[
                    mtpi.FECDISPX && mtpi.FECDISPX !== "0000-00-00" ? `Disponible ${fechaCorta(mtpi.FECDISPX)}` : null,
                    mtpi.AIPFECEN && mtpi.AIPFECEN !== "0000-00-00" ? `AIP ${fechaCorta(mtpi.AIPFECEN, mtpi.AIPHOREN)}` : null,
                    mtpi.FECHINSP && mtpi.FECHINSP !== "0000-00-00" ? `DIP habilitada ${fechaCorta(mtpi.FECHINSP, mtpi.HORHINSP)}` : null,
                    cabecera.FECCIERR ? `cierre ${fechaCorta(cabecera.FECCIERR.substring(0, 10), cabecera.FECCIERR.substring(11))}` : null,
                  ].filter(Boolean).join(" → ") || "—"}
                </div>
                <div className="iip-sub">AIP {String(mtpi.AIPESTXX || "—").toLowerCase()}</div>
              </div>
            </section>

            <section className="iip-strip">
              {[
                { t: "Productos", v: resumen.PRODUCTOS ?? productos.length, n: `${numero(resumen.UNIDADES_ESPERADAS)} unidades esperadas`, c: "NAVY" },
                { t: "Conformes", v: resumen.CONFORMES ?? 0, n: `${numero(resumen.UNIDADES_FISICAS)} unidades contadas`, c: "VERDE" },
                { t: "Faltantes", v: resumen.FALTANTES ?? 0, n: "físico menor que factura", c: "ROJO" },
                { t: "Sobrantes", v: resumen.SOBRANTES ?? 0, n: "físico mayor que factura", c: "NARANJA" },
                { t: "Sin conteo", v: resumen.SIN_CONTEO ?? 0, n: "sin registro físico", c: "GRIS" },
                { t: "Fotos", v: (resumen.FOTOS_PRODUCTO ?? 0) + (resumen.FOTOS_TAREA ?? 0), n: `${resumen.FOTOS_PRODUCTO ?? 0} de producto · ${resumen.FOTOS_TAREA ?? 0} de la tarea`, c: "AZUL" },
              ].map((tile) => (
                <div key={tile.t} className="rio-card iip-tile" style={{ "--tono": tono(tile.c).hex }}>
                  <span className="rio-kicker">{tile.t}</span>
                  <span className="iip-tile-valor">{tile.v}</span>
                  <span className="iip-tile-nota">{tile.n}</span>
                </div>
              ))}
            </section>

            <section className="rio-card">
              <div className="iip-seccion-cab">
                <div><div className="rio-card-title">Resultado por producto</div><div className="iip-sub">En el orden de la factura, faltantes y sobrantes primero. Clic en la fila abre las fotos, la ficha verificada en campo y los seriales.</div></div>
              </div>
              {productos.length === 0 ? (
                <div className="iip-vacio">La operación no tiene productos.</div>
              ) : (
                <div className="iip-wrap">
                  <table className="iip-tabla">
                    <thead>
                      <tr>
                        <th>Producto</th><th className="num">Según factura</th><th className="num">Físico</th><th className="num">Diferencia</th><th>Bulto</th><th>Resultado</th><th>Novedad y decisión</th><th className="num">Fotos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productos.map((p) => {
                        const r = RESULTADO[p.RESULTADO] ?? RESULTADO.SIN_CONTEO;
                        return (
                          <tr
                            key={`${p.PROIDXXX}-${p.SECITEXX}`}
                            className={`iip-fila ${p.RESULTADO?.toLowerCase() ?? ""}`}
                            onClick={() => setGaleria({ titulo: `${p.PRODESXX || p.DESCFACT} (${p.PROIDXXX})`, fotos: p.FOTOSXXX ?? [], descriptores: p.DESCRIPT ?? [], seriales: p.SERIALES ?? [], producto: p })}
                          >
                            <td><div className="iip-prod">{p.PRODESXX || p.DESCFACT || p.PROIDXXX}</div><div className="iip-sub">{p.PROIDXXX} · ítem {p.SECITEXX}{p.DESCFACT && p.DESCFACT !== p.PRODESXX ? ` · ${p.DESCFACT}` : ""}</div></td>
                            <td className="num">{numero(p.CANTESPX)}</td>
                            <td className="num">{numero(p.CANTFISX)}</td>
                            <td className={`num ${p.DIFERENC < 0 ? "dif-neg" : p.DIFERENC > 0 ? "dif-pos" : ""}`}>{p.DIFERENC === null || p.DIFERENC === undefined ? "—" : (p.DIFERENC > 0 ? "+" : "") + numero(p.DIFERENC)}</td>
                            <td>{p.BULTOXXX || "—"}</td>
                            <td><Chip tonoxxx={r.tonoxxx}>{r.texto}</Chip></td>
                            <td>
                              {p.NOVEDADX ? (
                                <>
                                  <b>{p.NOVEDADX === "FALTANTE" ? "Faltante" : p.NOVEDADX === "SOBRANTE" ? "Sobrante" : p.NOVEDADX} registrado</b>
                                  {p.DECISTXT && <div className="iip-sub">Decisión{p.DECFECXX ? ` ${fechaCorta(p.DECFECXX)}` : ""}: {p.DECISTXT}{p.NOVVIGEN === "NO" ? " · cerrada" : ""}</div>}
                                  {!p.DECISTXT && <div className="iip-sub">{p.ESTNOVXX === "BLOQUEADA" ? "Bloquea la operación hasta decidir" : p.ESTNOVXX?.toLowerCase()}</div>}
                                </>
                              ) : "—"}
                            </td>
                            <td className="num">{(p.FOTOSXXX ?? []).length > 0 ? <span className="iip-enlace">{p.FOTOSXXX.length}</span> : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="iip-dos">
              <div className="rio-card">
                <div className="rio-card-title">Acta y seguimientos del gestor</div>
                {acta.length === 0 ? (
                  <div className="iip-vacio">Sin anotaciones del gestor.</div>
                ) : (
                  <div className="iip-acta">
                    {acta.map((a, i) => (
                      <div key={i} className="iip-acta-item">
                        <div className="iip-acta-t">{fechaCorta(a.FECHA, a.HORA) || "—"}{a.USUARIO ? <div>{a.USUARIO}</div> : null}</div>
                        <div>{a.TEXTO}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="rio-card">
                <div className="rio-card-title">Fotos generales de la tarea · {fotosTarea.length}</div>
                {fotosTarea.length === 0 ? (
                  <div className="iip-vacio">Sin fotos generales.</div>
                ) : (
                  <div className="iip-fotos chicas" onClick={() => setGaleria({ titulo: "Fotos generales de la tarea", fotos: fotosTarea })}>
                    {fotosTarea.slice(0, 12).map((foto) => (
                      <img key={foto.IDARCHXX} src={foto.URLFOTOX} alt={`Foto ${foto.IDARCHXX}`} loading="lazy" />
                    ))}
                  </div>
                )}
                <div className="iip-sub">Clic abre la galería; dentro, cada foto se ve en grande sin salir del informe.</div>
              </div>
            </section>
          </>
        )}
        {galeria && <Galeria titulo={galeria.titulo} fotos={galeria.fotos} descriptores={galeria.descriptores} seriales={galeria.seriales} producto={galeria.producto} onCerrar={() => setGaleria(null)} />}
      </div>
    </div>
  );
};

export default Infoinsp;
